import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import { toast } from "react-hot-toast"; // Pour les notifications

const BackendUrl = process.env.REACT_APP_Backend_Url;
const COLORS = {
  orange: "#FF9800",
  lightGray: "#E0E0E0",
};

const Reviews = ({ productId, userName, setRate, setCountRate }) => {
  const [allComments, setAllComments] = useState([]);
  const [visibleComments, setVisibleComments] = useState(3);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [rating, setRating] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [ratingDistribution, setRatingDistribution] = useState({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  // Récupérer tous les commentaires
  useEffect(() => {
    if (productId) {
      fetchComments();
    }
  }, [productId]);

  const fetchComments = () => {
    axios
      .get(`${BackendUrl}/getAllCommenteProduitById/${productId}`)
      .then((response) => {
        setAllComments(response.data);
        setRate(response.data.length);
        calculateStats(response.data);
      })
      .catch((error) => {
        toast.error("Erreur lors de la récupération des commentaires");
        console.log(error);
      });
  };

  // Calculer les statistiques des commentaires
  const calculateStats = (comments) => {
    if (!comments || comments.length === 0) {
      setAverageRating(0);
      setCountRate(0);
      setRatingDistribution({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
      return;
    }

    // Calculer la moyenne des notes
    const total = comments.reduce((sum, comment) => sum + comment.etoil, 0);
    const avg = total / comments.length;
    setAverageRating(parseFloat(avg.toFixed(1)));
    setCountRate(parseFloat(avg.toFixed(1)));

    // Calculer la distribution des notes
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    comments.forEach((comment) => {
      const starRating = comment.etoil;
      distribution[starRating] = (distribution[starRating] || 0) + 1;
    });

    // Convertir en pourcentages
    const totalComments = comments.length;
    Object.keys(distribution).forEach((rating) => {
      distribution[rating] = Math.round(
        (distribution[rating] / totalComments) * 100
      );
    });

    setRatingDistribution(distribution);
  };

  // Soumettre un nouveau commentaire
  const submitComment = () => {
    if (!commentText.trim() || rating === 0) {
      toast.error("Veuillez saisir un commentaire et attribuer une note");
      return;
    }

    if (!userName) {
      toast.error("Vous devez être connecté pour laisser un avis");
      return;
    }

    axios
      .post(`${BackendUrl}/createCommenteProduit`, {
        description: commentText,
        clefProduct: productId,
        etoil: rating,
        userName: userName || "Utilisateur",
      })
      .then((resp) => {
        toast.success(resp.data.message || "Commentaire ajouté avec succès");
        setIsCommentOpen(false);
        setCommentText("");
        setRating(0);
        fetchComments();
      })
      .catch((error) => {
        toast.error(
          error.response?.data || "Erreur lors de l'ajout du commentaire"
        );
        console.log(error);
      });
  };

  // Afficher plus de commentaires
  const loadMoreComments = () => {
    setVisibleComments((prev) => prev + 3);
  };

  // Formater la date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("fr-FR", options);
  };

  // Fonction pour obtenir l'initiale du nom d'utilisateur
  const getUserInitial = (userName) => {
    return userName ? userName.charAt(0).toUpperCase() : "?";
  };

  return (
    <div className="space-y-8">
      {/* Résumé des avis */}
      <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center">
          <div className="text-5xl font-bold">{averageRating}</div>
          <div className="flex justify-center my-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className="h-5 w-5"
                style={{
                  color:
                    star <= Math.round(averageRating)
                      ? COLORS.orange
                      : COLORS.lightGray,
                }}
                fill={
                  star <= Math.round(averageRating) ? COLORS.orange : "none"
                }
              />
            ))}
          </div>
          <div className="text-sm text-gray-500">
            Basé sur {allComments.length} avis
          </div>
        </div>

        <div className="md:col-span-2">
          {[5, 4, 3, 2, 1].map((rating) => (
            <div key={rating} className="flex items-center mb-1">
              <span className="text-sm text-gray-600 w-4">{rating}</span>
              <Star
                className="h-4 w-4 mx-1"
                style={{ color: COLORS.orange }}
                fill={COLORS.orange}
              />
              <div className="flex-1 h-2 mx-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    backgroundColor: COLORS.orange,
                    width: `${ratingDistribution[rating]}%`,
                  }}
                ></div>
              </div>
              <span className="text-sm text-gray-500 w-8">
                {ratingDistribution[rating]}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Ajouter un commentaire */}
      {userName && !isCommentOpen && (
        <div className="text-center">
          <Button
            onClick={() => setIsCommentOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Ajouter un avis
          </Button>
        </div>
      )}

      {/* Formulaire pour ajouter un avis */}
      {isCommentOpen && (
        <div className="border p-4 rounded-lg">
          <h3 className="font-medium mb-2">Donnez votre avis</h3>
          <div className="mb-3">
            <div className="flex mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className="h-6 w-6 cursor-pointer"
                  style={{
                    color: star <= rating ? COLORS.orange : COLORS.lightGray,
                  }}
                  fill={star <= rating ? COLORS.orange : "none"}
                  onClick={() => setRating(star)}
                />
              ))}
            </div>
          </div>
          <textarea
            className="w-full p-2 border rounded-md mb-3"
            rows="3"
            placeholder="Partagez votre expérience avec ce produit..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          ></textarea>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsCommentOpen(false);
                setCommentText("");
                setRating(0);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={submitComment}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Publier
            </Button>
          </div>
        </div>
      )}

      {/* Avis individuels */}
      {allComments.length > 0 ? (
        <div className="space-y-6">
          {allComments.slice(0, visibleComments).map((comment, index) => (
            <div key={index} className="border-b border-gray-200 pb-6">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-lg font-medium mr-3">
                    {getUserInitial(comment.userName)}
                  </div>
                  <div>
                    <p className="font-medium">{comment.userName}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(comment.date)}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className="h-4 w-4"
                      style={{
                        color:
                          star <= comment.etoil
                            ? COLORS.orange
                            : COLORS.lightGray,
                      }}
                      fill={star <= comment.etoil ? COLORS.orange : "none"}
                    />
                  ))}
                </div>
              </div>
              <p className="text-gray-600">{comment.description}</p>
            </div>
          ))}

          {/* Bouton pour voir plus d'avis */}
          {allComments.length > visibleComments && (
            <div className="text-center">
              <Button
                variant="outline"
                className="border-gray-300 hover:bg-gray-50"
                onClick={loadMoreComments}
              >
                Voir plus d'avis
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Aucun avis pour ce produit. Soyez le premier à donner votre avis !
        </div>
      )}
    </div>
  );
};

export default Reviews;
